import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import React from 'react';

import type { PaywallPromptProps } from './PaywallPrompt.types';

import { Button } from '@/components/ui/Button';

/** Last-resort copy when the backend authored none (bare 402, no meta). */
const FALLBACK_TITLE = 'Upgrade to continue';
const FALLBACK_MESSAGE = 'This feature requires a plan upgrade.';
const FALLBACK_CTA = 'Upgrade';

/**
 * Presentation-only paywall renderer. It draws the backend-authored upgrade
 * copy — `upgradePrompt.{title,message,benefits,cta}` off a typed
 * `PaymentRequiredError` — behind a lock, with a single sakura upgrade CTA.
 *
 * The backend owns the copy; client strings are fallback-only (message falls
 * back to the error `.message`, then a generic line). The `variant` selects the
 * chrome only — `card` is a self-contained surface for the read/boundary shape,
 * `modal` is borderless content for `PaywallModal`'s dialog. Both chromes drive
 * the same content, so a new paywalled surface inherits this renderer for free.
 *
 * @example
 * ```tsx
 * <PaywallPrompt error={paymentError} variant="card" onUpgrade={goToUpgrade} />
 * ```
 */
export function PaywallPrompt({
  error,
  variant = 'card',
  onUpgrade,
  titleId,
  className = '',
  'data-testid': dataTestId,
}: PaywallPromptProps): React.JSX.Element {
  const { upgradePrompt, feature } = error;

  const title = upgradePrompt?.title ?? FALLBACK_TITLE;
  const message = upgradePrompt?.message ?? error.message ?? FALLBACK_MESSAGE;
  const cta = upgradePrompt?.cta ?? FALLBACK_CTA;
  const benefits =
    upgradePrompt?.benefits && upgradePrompt.benefits.length > 0
      ? upgradePrompt.benefits
      : (feature?.benefits ?? []);

  const isModal = variant === 'modal';

  const surfaceClasses = isModal
    ? 'text-center items-center'
    : 'rounded-xl border border-stroke-default bg-contrast-700 text-left items-start';

  return (
    <section
      className={`flex flex-col gap-4 p-6 ${surfaceClasses} ${className}`.trim()}
      data-testid={dataTestId}
    >
      <span
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sakura-wash text-primary-300"
        aria-hidden="true"
      >
        <LockClosedIcon className="h-6 w-6" />
      </span>

      <div className={`flex flex-col gap-2 ${isModal ? 'items-center' : ''}`}>
        <h3
          id={titleId}
          className="text-heading-sm font-semibold text-neutral-50"
        >
          {title}
        </h3>
        <p
          className={`text-body-md text-neutral-300 ${isModal ? 'max-w-sm' : ''}`.trim()}
        >
          {message}
        </p>
      </div>

      {benefits.length > 0 && (
        <ul
          className={`flex flex-col gap-2 ${isModal ? 'text-left' : ''}`.trim()}
        >
          {benefits.map((benefit) => (
            <li
              key={benefit}
              className="flex items-start gap-2 text-body-sm text-neutral-200"
            >
              <CheckIcon
                className="mt-0.5 h-4 w-4 shrink-0 text-primary-300"
                aria-hidden="true"
              />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      )}

      {/*
        Card uses `outline-primary`, modal uses solid `primary`. A Reports
        screen can show several locked panels at once, so a solid sakura CTA per
        card would put many competing primaries on one screen (the header
        already owns one). The modal is a single, focused CTA → solid.
      */}
      <Button
        variant={isModal ? 'primary' : 'outline-primary'}
        onClick={onUpgrade}
        fullWidth={isModal}
        className={isModal ? '' : 'self-start'}
      >
        {cta}
      </Button>
    </section>
  );
}

export default PaywallPrompt;
